// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
import type { CritterType, ModeRules } from './game';

export interface Mode {
  id: string;
  name: string;
  blurb: string;
  rules: ModeRules;
}

const SIX: readonly CritterType[] = ['spider', 'roach', 'fly', 'grub', 'rat', 'bat'];
const FIVE: readonly CritterType[] = ['spider', 'roach', 'fly', 'grub', 'rat'];
const FOUR: readonly CritterType[] = ['spider', 'roach', 'fly', 'grub'];

/**
 * Three modes with GENUINE spread (principle #14):
 *  - Parlour: the full social read — peek-and-pass ON, a big 6-critter deck, out at 4.
 *  - Snap: peek-and-pass OFF, so the card never travels the table — a fast, direct
 *    duel of reads. A structurally different game, not just a smaller number.
 *  - Swarm: fewer critters + out at 3, so danger is everywhere and the table gangs
 *    up fast — a knife fight where every claim is a threat.
 */
export const MODES: Record<string, Mode> = {
  parlour: {
    id: 'parlour',
    name: 'Parlour',
    blurb: 'The classic read. Peek & pass a card round the table; out at four of a kind.',
    rules: { types: SIX, copiesBase: 4, copiesPerPlayer: 2, threshold: 4, peek: true, peekBudget: 1, decisionMs: 30_000 },
  },
  snap: {
    id: 'snap',
    name: 'Snap',
    blurb: 'No passing on — believe or doubt, every card, right now. A fast lie-detector duel.',
    rules: { types: FIVE, copiesBase: 6, copiesPerPlayer: 2, threshold: 4, peek: false, peekBudget: 0, decisionMs: 22_000 },
  },
  swarm: {
    id: 'swarm',
    name: 'Swarm',
    blurb: 'Four critters, out at THREE. Danger everywhere and the table piling on — brutal and fast.',
    rules: { types: FOUR, copiesBase: 4, copiesPerPlayer: 2, threshold: 3, peek: true, peekBudget: 1, decisionMs: 25_000 },
  },
};

export const MODE_IDS = Object.keys(MODES);
export const DEFAULT_MODE = 'parlour';

/** Validate an id off the wire — an unknown id falls back, never reaches the deal as undefined. */
export function modeOf(id: unknown): Mode {
  if (typeof id === 'string' && Object.hasOwn(MODES, id)) return MODES[id];
  return MODES[DEFAULT_MODE];
}
